import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Card } from "../components/ui/card";
import { Plus, Shirt, Trash2, Edit, X, ExternalLink, RefreshCw, MoreVertical, Crown } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Wardrobe() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [pendingItemData, setPendingItemData] = useState(null); // Store item for after upgrade
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    brand: "",
    item_name: "",
    description: "",
    total_cost: "",
    category: "clothing",
    purchase_date: "",
    image_base64: null,
    // Watch-specific fields
    watch_model: "",
    watch_reference: "",
    watch_year: "",
    watch_case_material: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");

  // Memoize fetchItems to avoid dependency issues
  const fetchItems = useCallback(async () => {
    try {
      // Add cache-busting timestamp to prevent CDN caching
      const timestamp = Date.now();
      const response = await authFetch(`${API_URL}/api/wardrobe?_t=${timestamp}`);
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error("Failed to fetch wardrobe:", error);
      toast.error("Failed to load wardrobe");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    
    // Refresh data when page becomes visible (user switches back to tab/app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchItems();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchItems]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(",")[1];
        setFormData({ ...formData, image_base64: base64 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        brand: formData.brand,
        item_name: formData.item_name,
        description: formData.description || null,
        total_cost: parseFloat(formData.total_cost),
        category: formData.category,
        purchase_date: formData.purchase_date || null,
        image_base64: formData.image_base64,
      };

      // Add watch-specific fields if category is watch
      if (formData.category === "watch") {
        payload.watch_model = formData.watch_model || null;
        payload.watch_reference = formData.watch_reference || null;
        payload.watch_year = formData.watch_year ? parseInt(formData.watch_year) : null;
        payload.watch_case_material = formData.watch_case_material || null;
      }

      let response;
      if (editingItem) {
        response = await authFetch(`${API_URL}/api/wardrobe/${editingItem.item_id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        response = await authFetch(`${API_URL}/api/wardrobe`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      if (response.ok) {
        toast.success(editingItem ? "Item updated" : "Item added to wardrobe");
        fetchItems();
        resetForm();
        setDialogOpen(false);
      } else {
        // Parse error response safely
        let errorData = {};
        let rawText = "";
        try {
          rawText = await response.text();
          errorData = JSON.parse(rawText);
        } catch (e) {
          console.error("Error parsing response:", e, "Raw:", rawText);
        }
        
        console.log("API Error:", response.status, errorData);
        
        // Check for item limit error - handle both nested and flat structures
        const detail = errorData.detail;
        const isLimitError = 
          response.status === 403 ||
          (typeof detail === 'object' && detail?.error === "item_limit_reached") ||
          (typeof detail === 'object' && detail?.upgrade_required) ||
          (typeof detail === 'string' && detail.toLowerCase().includes("limit"));
        
        if (isLimitError) {
          // Save the pending item data for after upgrade
          setPendingItemData(payload);
          setDialogOpen(false);
          setUpgradeModalOpen(true);
        } else {
          // Show actual error for debugging
          const message = typeof detail === 'object' 
            ? (detail?.message || JSON.stringify(detail))
            : (detail || `Error ${response.status}`);
          toast.error(message);
        }
      }
    } catch (error) {
      console.error("Error saving item:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      const response = await authFetch(`${API_URL}/api/wardrobe/${itemId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Item deleted");
        fetchItems();
      } else {
        throw new Error("Failed to delete");
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      brand: item.brand,
      item_name: item.item_name,
      description: item.description || "",
      total_cost: item.total_cost.toString(),
      category: item.category || "clothing",
      purchase_date: item.purchase_date || "",
      image_base64: item.image_base64,
      // Watch-specific fields
      watch_model: item.watch_model || "",
      watch_reference: item.watch_reference || "",
      watch_year: item.watch_year ? item.watch_year.toString() : "",
      watch_case_material: item.watch_case_material || "",
    });
    setDialogOpen(true);
  };

  const handleRefreshMarketValue = async (itemId) => {
    try {
      toast.info("Refreshing market value...");
      const response = await authFetch(`${API_URL}/api/wardrobe/${itemId}/refresh-market-value`, {
        method: "POST",
      });
      if (response.ok) {
        toast.success("Market value updated");
        fetchItems();
      } else {
        throw new Error("Failed to refresh");
      }
    } catch (error) {
      console.error("Error refreshing market value:", error);
      toast.error("Failed to refresh market value");
    }
  };

  const getListingUrls = (item) => {
    const searchQuery = encodeURIComponent(`${item.brand} ${item.item_name}`);
    return {
      grailed: `https://www.grailed.com/sell`,
      grailed_search: `https://www.grailed.com/shop?query=${searchQuery}`,
      depop: `https://www.depop.com/sell/`,
      depop_search: `https://www.depop.com/search/?q=${searchQuery}`,
      poshmark: `https://poshmark.com/create-listing`,
      poshmark_search: `https://poshmark.com/search?query=${searchQuery}&type=listings`,
    };
  };

  const resetForm = () => {
    setFormData({
      brand: "",
      item_name: "",
      description: "",
      total_cost: "",
      category: "clothing",
      purchase_date: "",
      image_base64: null,
      watch_model: "",
      watch_reference: "",
      watch_year: "",
      watch_case_material: "",
    });
    setEditingItem(null);
  };

  const filteredItems = filterCategory === "all" 
    ? items 
    : items.filter(item => item.category === filterCategory);

  const categories = ["all", ...new Set(items.map(item => item.category || "clothing"))];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="wardrobe-page">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl tracking-tight text-primary mb-2">
                Wardrobe
              </h1>
              <p className="text-muted-foreground">
                {items.length} items in your collection
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { setLoading(true); fetchItems(); }}
              className="h-8 w-8"
              title="Refresh"
              data-testid="refresh-wardrobe-btn"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-12 px-6 font-medium tracking-wide text-sm uppercase"
                data-testid="add-item-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">
                  {editingItem ? "Edit Item" : "Add New Item"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="brand">Brand *</Label>
                  <Input
                    id="brand"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="e.g., A.P.C., Acne Studios"
                    required
                    data-testid="item-brand-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="item_name">Item Name *</Label>
                  <Input
                    id="item_name"
                    value={formData.item_name}
                    onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                    placeholder="e.g., Wool Sweater, Leather Jacket"
                    required
                    data-testid="item-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="total_cost">Total Cost ($) *</Label>
                  <Input
                    id="total_cost"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.total_cost}
                    onChange={(e) => setFormData({ ...formData, total_cost: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="item-cost-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="item-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clothing">Clothing</SelectItem>
                      <SelectItem value="outerwear">Outerwear</SelectItem>
                      <SelectItem value="footwear">Footwear</SelectItem>
                      <SelectItem value="watch">Watch</SelectItem>
                      <SelectItem value="accessory">Accessory</SelectItem>
                      <SelectItem value="bag">Bag</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="purchase_date">Purchase Date</Label>
                  <Input
                    id="purchase_date"
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    data-testid="item-purchase-date-input"
                  />
                </div>

                {/* Watch-specific fields - only show when category is watch */}
                {formData.category === "watch" && (
                  <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
                    <p className="text-xs text-muted-foreground font-medium">Watch Details (for accurate Chrono24 valuation)</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="watch_model">Model</Label>
                        <Input
                          id="watch_model"
                          value={formData.watch_model}
                          onChange={(e) => setFormData({ ...formData, watch_model: e.target.value })}
                          placeholder="e.g., GMT Master II"
                          data-testid="watch-model-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="watch_reference">Reference Number</Label>
                        <Input
                          id="watch_reference"
                          value={formData.watch_reference}
                          onChange={(e) => setFormData({ ...formData, watch_reference: e.target.value })}
                          placeholder="e.g., 126710BLNR"
                          data-testid="watch-reference-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="watch_year">Year</Label>
                        <Input
                          id="watch_year"
                          type="number"
                          value={formData.watch_year}
                          onChange={(e) => setFormData({ ...formData, watch_year: e.target.value })}
                          placeholder="e.g., 2020"
                          min="1900"
                          max={new Date().getFullYear()}
                          data-testid="watch-year-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="watch_case_material">Case Material</Label>
                        <Select
                          value={formData.watch_case_material}
                          onValueChange={(value) => setFormData({ ...formData, watch_case_material: value })}
                        >
                          <SelectTrigger data-testid="watch-material-select">
                            <SelectValue placeholder="Select material" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="stainless steel">Stainless Steel</SelectItem>
                            <SelectItem value="yellow gold">Yellow Gold</SelectItem>
                            <SelectItem value="white gold">White Gold</SelectItem>
                            <SelectItem value="rose gold">Rose Gold</SelectItem>
                            <SelectItem value="platinum">Platinum</SelectItem>
                            <SelectItem value="titanium">Titanium</SelectItem>
                            <SelectItem value="ceramic">Ceramic</SelectItem>
                            <SelectItem value="two-tone">Two-Tone</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Color, size, material, etc."
                    rows={3}
                    data-testid="item-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="image">Image (optional)</Label>
                  <Input
                    id="image"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    className="cursor-pointer"
                    data-testid="item-image-input"
                  />
                  {formData.image_base64 && (
                    <div className="relative w-20 h-20 mt-2">
                      <img
                        src={`data:image/jpeg;base64,${formData.image_base64}`}
                        alt="Preview"
                        className="w-full h-full object-cover rounded-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_base64: null })}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-12 font-medium tracking-wide text-sm uppercase"
                  data-testid="save-item-btn"
                >
                  {submitting ? "Saving..." : editingItem ? "Update Item" : "Add Item"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Upgrade Modal */}
          <Dialog open={upgradeModalOpen} onOpenChange={setUpgradeModalOpen}>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl flex items-center gap-2 text-center justify-center">
                  <Crown className="w-6 h-6 text-amber-500" />
                  Your Starter Closet is Full
                </DialogTitle>
                <DialogDescription className="text-center text-muted-foreground">
                  You've reached the 10-item limit on the free plan. Upgrade to add more items and unlock powerful resale and wardrobe insights.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Tier Options */}
                <div className="space-y-3">
                  {/* Core Tier - Recommended */}
                  <div className="relative border-2 border-terracotta-500 rounded-lg p-4 bg-terracotta-50/30">
                    <div className="absolute -top-2.5 left-4 bg-terracotta-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                      RECOMMENDED
                    </div>
                    <div className="flex justify-between items-start mt-1">
                      <div>
                        <h4 className="font-heading font-semibold text-lg">Full Closet</h4>
                        <p className="text-xs text-muted-foreground">For wardrobe optimizers</p>
                      </div>
                      <div className="text-right">
                        <p className="font-heading text-xl font-bold text-terracotta-500">$5.99<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
                        <p className="text-xs text-muted-foreground">or $59/year</p>
                      </div>
                    </div>
                    <ul className="mt-3 space-y-1.5 text-sm">
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-terracotta-500 rounded-full"></span>
                        Unlimited clothing items
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-terracotta-500 rounded-full"></span>
                        Full cost-per-wear analytics
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-terracotta-500 rounded-full"></span>
                        AI outfit recognition
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-terracotta-500 rounded-full"></span>
                        Resale marketplace integration
                      </li>
                    </ul>
                    <Button
                      onClick={() => {
                        // Store pending item in localStorage for after upgrade
                        if (pendingItemData) {
                          localStorage.setItem("wornwell_pending_item", JSON.stringify(pendingItemData));
                        }
                        setUpgradeModalOpen(false);
                        navigate("/subscription?plan=core");
                      }}
                      className="w-full mt-4 bg-terracotta-500 hover:bg-terracotta-600 text-white"
                      data-testid="upgrade-core-btn"
                    >
                      Upgrade to Core
                    </Button>
                  </div>
                  
                  {/* Pro Tier */}
                  <div className="border border-border rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-heading font-semibold text-lg">Wardrobe Intelligence</h4>
                        <p className="text-xs text-muted-foreground">For fashion enthusiasts</p>
                      </div>
                      <div className="text-right">
                        <p className="font-heading text-xl font-bold">$14.99<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
                        <p className="text-xs text-muted-foreground">or $149/year</p>
                      </div>
                    </div>
                    <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-muted-foreground rounded-full"></span>
                        Everything in Core, plus:
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-muted-foreground rounded-full"></span>
                        "Best time to sell" signals
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-muted-foreground rounded-full"></span>
                        Advanced analytics & projections
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-muted-foreground rounded-full"></span>
                        AI outfit suggestions
                      </li>
                    </ul>
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (pendingItemData) {
                          localStorage.setItem("wornwell_pending_item", JSON.stringify(pendingItemData));
                        }
                        setUpgradeModalOpen(false);
                        navigate("/subscription?plan=pro");
                      }}
                      className="w-full mt-4"
                      data-testid="upgrade-pro-btn"
                    >
                      Upgrade to Pro
                    </Button>
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  onClick={() => setUpgradeModalOpen(false)}
                  className="w-full text-muted-foreground text-sm"
                >
                  Maybe Later
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filter */}
        {categories.length > 2 && (
          <div className="flex gap-2 flex-wrap">
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={filterCategory === cat ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterCategory(cat)}
                className={`rounded-sm capitalize ${
                  filterCategory === cat 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-white hover:bg-sand-50"
                }`}
                data-testid={`filter-${cat}`}
              >
                {cat}
              </Button>
            ))}
          </div>
        )}

        {/* Items Grid */}
        {filteredItems.length > 0 ? (
          <div className="masonry-grid">
            {filteredItems.map((item) => (
              <Card 
                key={item.item_id} 
                className="border border-border shadow-none overflow-hidden card-hover group"
                data-testid={`wardrobe-item-${item.item_id}`}
              >
                <div className="aspect-clothing relative bg-sand-50">
                  {item.image_base64 ? (
                    <img
                      src={`data:image/jpeg;base64,${item.image_base64}`}
                      alt={item.item_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Shirt className="w-12 h-12 text-sand-300" />
                    </div>
                  )}
                  {/* Cost per wear badge */}
                  <div className="cpw-badge">
                    ${item.cost_per_wear?.toFixed(2) || item.total_cost?.toFixed(2)}/wear
                  </div>
                  {/* Actions overlay */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <Button
                      size="icon"
                      variant="secondary"
                      className="w-8 h-8 bg-white/90 backdrop-blur-sm"
                      onClick={() => handleEdit(item)}
                      data-testid={`edit-item-${item.item_id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon"
                          variant="secondary"
                          className="w-8 h-8 bg-white/90 backdrop-blur-sm"
                          data-testid={`more-actions-${item.item_id}`}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => handleRefreshMarketValue(item.item_id)}>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Refresh Value
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <a href={getListingUrls(item).grailed} target="_blank" rel="noopener noreferrer" className="flex items-center">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            List on Grailed
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a href={getListingUrls(item).depop} target="_blank" rel="noopener noreferrer" className="flex items-center">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            List on Depop
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a href={getListingUrls(item).poshmark} target="_blank" rel="noopener noreferrer" className="flex items-center">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            List on Poshmark
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <a href={getListingUrls(item).grailed_search} target="_blank" rel="noopener noreferrer" className="flex items-center text-muted-foreground">
                            View on Grailed
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a href={getListingUrls(item).depop_search} target="_blank" rel="noopener noreferrer" className="flex items-center text-muted-foreground">
                            View on Depop
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a href={getListingUrls(item).poshmark_search} target="_blank" rel="noopener noreferrer" className="flex items-center text-muted-foreground">
                            View on Poshmark
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleDelete(item.item_id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Item
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-1">
                    {item.brand}
                  </p>
                  <p className="font-medium text-primary truncate">{item.item_name}</p>
                  <div className="flex items-center justify-between mt-2 text-sm text-muted-foreground">
                    <span>${item.total_cost?.toFixed(2)}</span>
                    <span>{item.wear_count || 0} wears</span>
                  </div>
                  {/* Market Value */}
                  {item.market_value && (
                    <div className="mt-2 pt-2 border-t border-border">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Market Value</span>
                        <span className="text-xs font-medium text-terracotta-500">
                          approx. ${item.market_value.estimated_value?.toFixed(0)}
                        </span>
                      </div>
                      {item.market_value.listings_found > 0 && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          Based on {item.market_value.listings_found} listings
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-sand-50 rounded-sm">
            <Shirt className="w-12 h-12 mx-auto mb-4 text-sand-300" />
            <h3 className="font-heading text-xl text-primary mb-2">Your wardrobe is empty</h3>
            <p className="text-muted-foreground mb-6">Start by adding your first item</p>
            <Button 
              onClick={() => setDialogOpen(true)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-12 px-6 font-medium tracking-wide text-sm uppercase"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
